import { GLManager } from '@play-co/timestep-core/renderer';
import {
  CustomFilter,
  ImageView,
  View,
  type ViewOpts,
} from '@play-co/timestep-core/ui';
import { HueShift, getHueShift } from 'src/game/cards/shaders/HueShift';
import { dreamDriftGetHeroColor } from 'src/game/dreamDrift/dreamDrift.costumes';
import type { DreamDriftHero } from 'src/replicant/dreamDrift/dreamDrift.ruleset';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';
import DreamDriftHeroIcon from './DreamDriftHeroIcon';

/** Dominant neutral border color used as the source for every hero hue shift. */
const AVATAR_PIN_BORDER_SOURCE_COLOR = '#FF0000';

/** Values required to display a player portrait and hero inside the avatar pin. */
type DreamDriftAvatarPinSetupOpts = {
  /** Player avatar image URL or asset path. */
  avatar: string;
  /** Hero selected for the current Dream Drift progression segment. */
  hero?: DreamDriftHero;
};

/** Map pin that displays a masked player avatar behind its decorative outline. */
export default class DreamDriftAvatarPin extends View {
  /** Portrait image rendered beneath the pin outline. */
  private readonly avatar: ImageView;

  /** Shader rotating the neutral border hue toward the current hero color. */
  private readonly borderHueShift?: HueShift;

  /** Hero badge centered over the top edge of the pin artwork. */
  private readonly heroIcon: DreamDriftHeroIcon;

  /** Artwork container offset above the pin's map-position anchor. */
  private container: View;

  /**
   * Create an unconfigured avatar pin at its requested map position.
   *
   * @param opts Parent, position, scale, and other base view options.
   */
  constructor(opts: ViewOpts = {}) {
    // Anchor this view at the map node position represented by the pin tip.
    super({
      ...opts,
      anchorX: 0.5,
      anchorY: 1,
    });

    // Position the complete pin artwork directly above the map anchor.
    this.container = new View({
      superview: this,
      width: 172,
      height: 200,
      x: -86,
      y: -200,
    });

    // Start with the generic portrait until setup provides the player's avatar.
    this.avatar = new ImageView({
      superview: this.container,
      image: GENERIC_AVATAR,
      width: 140,
      height: 140,
      x: 15,
      y: 15,
    });

    // Create one reusable shader when the current renderer supports custom filters.
    this.borderHueShift = GLManager.isSupported
      ? GLManager.createShader(HueShift)
      : undefined;

    // Layer the supplied pin artwork above the portrait and mask.
    new ImageView({
      superview: this.container,
      image: 'assets/ui/dreamDrift/common/avatar_pin.png',
      width: 172,
      height: 200,
      filter: this.borderHueShift
        ? new CustomFilter(this.borderHueShift)
        : undefined,
    });

    // Layer the current hero badge over the middle of the pin's top edge.
    this.heroIcon = new DreamDriftHeroIcon({
      superview: this.container,
      x: 86,
      y: 0,
    });

    // Update the artwork position every frame for continuous floating motion.
    this.setTick(this.tick.bind(this));
  }

  /** Float the pin artwork through a ten-pixel vertical range. */
  public tick(): void {
    // Convert elapsed browser time into a smooth repeating vertical offset.
    const floatOffset = Math.sin(performance.now() / 500) * 5;

    // Move the artwork around its resting position while preserving the map anchor.
    this.container.updateOpts({ y: -200 + floatOffset });
  }

  /**
   * Display the player avatar and selected hero on the pin.
   *
   * @param opts Player avatar and current hero selection to display.
   */
  public setup({ avatar, hero }: DreamDriftAvatarPinSetupOpts): void {
    // Keep the pin populated when the player has no custom avatar image.
    this.avatar.setImage(avatar || GENERIC_AVATAR);

    // Synchronize the pin styling with the current hero selection.
    this.setHero(hero);
  }

  /**
   * Update the hero badge and border color without replacing the avatar.
   *
   * @param hero Currently selected Dream Drift hero, when available.
   * @returns Nothing.
   */
  public setHero(hero?: DreamDriftHero): void {
    // Synchronize the optional badge with the current hero selection.
    this.heroIcon.setup({ hero });

    // Renderers without custom shader support retain the original border.
    if (!this.borderHueShift) return;

    // Missing selections reset the border to its original neutral hue.
    if (!hero) {
      this.borderHueShift.shift = 0;
      return;
    }

    // Rotate the measured source hue toward the selected hero's dominant color.
    this.borderHueShift.shift = getHueShift(
      AVATAR_PIN_BORDER_SOURCE_COLOR,
      dreamDriftGetHeroColor(hero),
    );
  }
}
